import { CATALOG_PAGE_SIZE, catalogPageParams } from "./catalogPageSize";

async function resolveTotalItemCount({
	fetchPage,
	baseParams,
	listKey,
	apiMaxPage,
	apiPageSize,
	probe,
}) {
	if (apiMaxPage <= 1) {
		return (probe[listKey] ?? []).length;
	}
	const lastResponse = await fetchPage({
		...baseParams,
		...catalogPageParams(apiMaxPage),
	});
	const lastLen = (lastResponse[listKey] ?? []).length;
	return (apiMaxPage - 1) * apiPageSize + lastLen;
}

/**
 * Server may still paginate by 10 while UI shows 12 (4×3). Merges API pages when needed.
 */
export async function fetchCatalogListPage({
	fetchPage,
	baseParams,
	displayPage,
	listKey,
}) {
	const displaySize = CATALOG_PAGE_SIZE;
	const probe = await fetchPage({
		...baseParams,
		...catalogPageParams(1),
	});
	const probeList = probe[listKey] ?? [];
	const apiMaxPage = probe.max_page ?? 1;

	let apiPageSize = probeList.length;
	if (apiMaxPage > 1) {
		if (probeList.length >= displaySize) {
			apiPageSize = displaySize;
		} else if (probeList.length >= 10) {
			apiPageSize = 10;
		}
	}
	if (apiPageSize < 1) {
		apiPageSize = displaySize;
	}

	if (apiPageSize === displaySize) {
		const response =
			displayPage === 1
				? probe
				: await fetchPage({
						...baseParams,
						...catalogPageParams(displayPage),
					});
		return {
			items: response[listKey] ?? [],
			maxPage: response.max_page ?? 1,
		};
	}

	const totalItems = await resolveTotalItemCount({
		fetchPage,
		baseParams,
		listKey,
		apiMaxPage,
		apiPageSize,
		probe,
	});
	const maxPage = Math.max(1, Math.ceil(totalItems / displaySize));

	const globalStart = (displayPage - 1) * displaySize;
	if (globalStart >= totalItems) {
		return { items: [], maxPage };
	}

	const firstApiPage = Math.floor(globalStart / apiPageSize) + 1;
	const lastApiPage = Math.floor((globalStart + displaySize - 1) / apiPageSize) + 1;

	let merged = [];
	for (let apiPage = firstApiPage; apiPage <= lastApiPage; apiPage++) {
		const useProbe = apiPage === 1 && firstApiPage === 1;
		const response = useProbe
			? probe
			: await fetchPage({
					...baseParams,
					...catalogPageParams(apiPage),
				});
		merged = merged.concat(response[listKey] ?? []);
	}

	const offsetInMerged = globalStart - (firstApiPage - 1) * apiPageSize;
	const items = merged.slice(offsetInMerged, offsetInMerged + displaySize);

	return { items, maxPage };
}
